import { useState, useEffect, useCallback, useRef } from 'react';
import React from 'react';

export const useParticipants = (room) => {
  const [remoteParticipants, setRemoteParticipants] = useState([]);
  const [participantVideoRefs, setParticipantVideoRefs] = useState({});
  const participantVideoRefsRef = useRef({});

  // Keep ref in sync with state
  useEffect(() => {
    participantVideoRefsRef.current = participantVideoRefs;
  }, [participantVideoRefs]);

  // Helper function to get consistent participant ID
  const getParticipantId = useCallback((participant) => {
    // For local participant, use 'local' as identifier
    if (participant === room?.localParticipant) {
      return 'local';
    }
    // For remote participants, use sid first, then identity
    return participant.sid || participant.identity || 'unknown';
  }, [room]);

  // Helper to get/create a ref for a participant
  const getParticipantVideoRef = useCallback((participantId) => {
    const currentRefs = participantVideoRefsRef.current;
    if (!currentRefs[participantId]) {
      const newRef = React.createRef();
      const newRefs = { ...currentRefs, [participantId]: newRef };
      participantVideoRefsRef.current = newRefs;
      setParticipantVideoRefs(newRefs);
      console.log('Created new ref for participant:', participantId);
      return newRef;
    }
    return currentRefs[participantId];
  }, []);

  // Attach track to correct video element
  const attachTrack = useCallback((track, participant) => {
    const participantId = getParticipantId(participant);
    const videoRef = getParticipantVideoRef(participantId);
    console.log('Attaching track:', track.kind, 'for participant:', participant.identity, 'participantId:', participantId, 'videoRef exists:', !!videoRef.current);
    
    if (videoRef.current) {
      if (track.kind === 'video') {
        track.attach(videoRef.current);
        console.log('Video track attached successfully');
      } else if (track.kind === 'audio') {
        // For audio tracks, we can attach them to the video element as well
        track.attach(videoRef.current);
        console.log('Audio track attached successfully');
      }
    } else {
      console.warn('Video ref not found for participant:', participantId);
      // Retry attachment after a short delay
      setTimeout(() => {
        const retryRef = getParticipantVideoRef(participantId);
        if (retryRef.current) {
          track.attach(retryRef.current);
          console.log('Track attached on retry for participant:', participantId);
        }
      }, 100);
    }
  }, [getParticipantVideoRef, getParticipantId]);

  const detachTrack = useCallback((track, participant) => {
    const participantId = getParticipantId(participant);
    const videoRef = getParticipantVideoRef(participantId);
    console.log('Detaching track:', track.kind, 'for participant:', participant.identity, 'participantId:', participantId);
    if (videoRef.current) {
      if (track.kind === 'video' || track.kind === 'audio') {
        track.detach(videoRef.current);
        console.log('Track detached successfully');
      }
    }
  }, [getParticipantVideoRef, getParticipantId]);

  const setupParticipantContainer = useCallback((participant, room) => {
    // Update remote participants list
    setRemoteParticipants(Array.from(room.participants.values()));
  }, []);

  const trackPublished = useCallback((publication, participant) => {
    if (publication.track && publication.track.kind !== 'data') {
      attachTrack(publication.track, participant);
    }
    
    publication.on('subscribed', track => {
      if (track.kind !== 'data') {
        attachTrack(track, participant);
      }
    });
    
    publication.on('unsubscribed', track => {
      if (track.kind !== 'data') {
        detachTrack(track, participant);
      }
    });
  }, [attachTrack, detachTrack]);

  const participantConnected = useCallback((participant, room) => {
    console.log('Participant connected:', participant.identity);
    
    // Set up the Participant's media container
    setupParticipantContainer(participant, room);

    // Tracks already published
    participant.tracks.forEach(publication => {
      trackPublished(publication, participant);
    });

    // Tracks that will be published later
    participant.on('trackPublished', publication => {
      trackPublished(publication, participant);
    });
  }, [setupParticipantContainer, trackPublished]);

  const participantDisconnected = useCallback((participant, room) => {
    console.log('Participant disconnected:', participant.identity);
    
    // Clean up participant's media
    participant.tracks.forEach(publication => {
      if (publication.track && publication.track.kind !== 'data') {
        detachTrack(publication.track, participant);
      }
    });
    
    // Update remote participants list
    setRemoteParticipants(Array.from(room.participants.values()));
  }, [detachTrack]);

  // Handle participant events
  useEffect(() => {
    if (!room) return;

    // Handle the LocalParticipant's media
    participantConnected(room.localParticipant, room);

    // Subscribe to RemoteParticipants already in the Room
    room.participants.forEach(participant => {
      participantConnected(participant, room);
    });

    // Subscribe to RemoteParticipants joining later
    const handleParticipantConnected = (participant) => {
      participantConnected(participant, room);
    };

    // Handle a disconnected RemoteParticipant
    const handleParticipantDisconnected = (participant) => {
      participantDisconnected(participant, room);
    };

    room.on('participantConnected', handleParticipantConnected);
    room.on('participantDisconnected', handleParticipantDisconnected);

    // Cleanup
    return () => {
      room.off('participantConnected', handleParticipantConnected);
      room.off('participantDisconnected', handleParticipantDisconnected);
    };
  }, [room, participantConnected, participantDisconnected]);

  // Effect to ensure all existing tracks are properly attached
  useEffect(() => {
    if (!room) return;

    // Give the DOM a chance to render video elements before attaching tracks
    const attachExistingTracks = () => {
      console.log('Attaching existing tracks...');
      
      // Attach local participant tracks
      if (room.localParticipant) {
        room.localParticipant.tracks.forEach(publication => {
          if (publication.track && publication.track.kind === 'video') {
            attachTrack(publication.track, room.localParticipant);
          }
        });
      }

      // Attach remote participant tracks
      room.participants.forEach(participant => {
        participant.tracks.forEach(publication => {
          if (publication.track && (publication.track.kind === 'video' || publication.track.kind === 'audio')) {
            attachTrack(publication.track, participant);
          }
        });
      });
    };

    // Use a small delay to ensure video elements are rendered
    const timeoutId = setTimeout(attachExistingTracks, 100);
    return () => clearTimeout(timeoutId);
  }, [room, attachTrack]);

  // Get all participants for rendering
  const allParticipants = room ? [room.localParticipant, ...Array.from(room.participants.values())] : [];

  return {
    remoteParticipants,
    allParticipants,
    participantVideoRefs,
    getParticipantVideoRef,
    getParticipantId,
    attachTrack,
    detachTrack,
    participantConnected,
    participantDisconnected
  };
}; 